// The playable game: a self-contained component that mounts its own DOM
// (canvas + HUD) into a container and runs one PlayableStory. Kept vanilla TS
// — Preact is used only for the hub/editors.

import { SceneManager } from "@/engine/SceneManager";
import { NarrativeEngine } from "@/narrative/NarrativeEngine";
import { TypewriterEffect } from "@/components/TypewriterEffect";
import { StorageService } from "@/persistence/StorageService";
import { SaveManager, saveKeyFor } from "@/persistence/SaveManager";
import { RunManager } from "@/roguelike/RunManager";
import { MetaStore } from "@/roguelike/MetaStore";
import { createDevScene, type DevScene } from "@/scenes/DevScene";
import type { SaveData } from "@/types";
import type { PlayableStory } from "@/story/playable";

const DEFAULT_BG = "#1a1a2e";
const MAX_HEALTH = 100;
const AUTO_SAVE_DEBOUNCE_MS = 800;

export interface GameOptions {
  storage: StorageService;
  onExit?: () => void;
}

const HUD_HTML = `
  <div id="status-bar">
    <span id="hp-label">HP</span>
    <span class="bar"><span id="hp-fill"></span></span>
    <span id="hp-value"></span>
    <span id="story-title"></span>
    <span id="status-tags"></span>
    <button id="exit-btn" title="Back to the hub">Exit</button>
    <button id="save-btn" title="Save progress">Save</button>
  </div>
  <div id="toast"></div>
  <div id="ui-overlay">
    <div id="narrative-box">
      <div id="narrative-text"></div>
      <div id="choices-container"></div>
    </div>
  </div>
`;

export class Game {
  private readonly story: PlayableStory;
  private readonly container: HTMLElement;
  private readonly storage: StorageService;
  private readonly onExit?: () => void;

  private sceneManager: SceneManager | null = null;
  private narrativeEngine: NarrativeEngine | null = null;
  private typewriter: TypewriterEffect | null = null;
  private saveManager: SaveManager | null = null;
  private metaStore: MetaStore;
  private runManager: RunManager | null = null;
  private devScene: DevScene | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private currentSceneKey: string | null = null;
  private currentFloorName: string | null = null;
  private pendingFloorRequest = false;
  private bossStarted = false;
  private ending: string | null = null;
  private lastSaveAt = 0;
  private disposed = false;

  constructor(container: HTMLElement, story: PlayableStory, opts: GameOptions) {
    this.container = container;
    this.story = story;
    this.storage = opts.storage;
    this.onExit = opts.onExit;
    this.metaStore = new MetaStore(this.storage);
  }

  async start(): Promise<void> {
    this.disposed = false;

    this.container.innerHTML = `<canvas id="game-canvas"></canvas>${HUD_HTML}`;
    const canvas = this.container.querySelector(
      "#game-canvas"
    ) as HTMLCanvasElement;

    this.sceneManager = new SceneManager(canvas);
    this.narrativeEngine = new NarrativeEngine();
    this.narrativeEngine.loadStory(this.story.inkJson);

    const textElement = this.container.querySelector(
      "#narrative-text"
    ) as HTMLElement;
    this.typewriter = new TypewriterEffect(textElement, 14);
    this.saveManager = new SaveManager(this.storage, saveKeyFor(this.story.id));
    this.runManager =
      this.story.schema === "roguelike"
        ? new RunManager(this.story.floors)
        : null;

    const saveBtn = this.container.querySelector("#save-btn");
    saveBtn?.addEventListener("click", () => this.manualSave());
    const exitBtn = this.container.querySelector("#exit-btn");
    exitBtn?.addEventListener("click", () => this.onExit?.());

    const devScene = createDevScene();
    this.devScene = devScene;
    this.sceneManager.addObject(devScene.group);
    this.sceneManager.setBackgroundColor(DEFAULT_BG);

    const titleEl = this.container.querySelector("#story-title");
    if (titleEl) titleEl.textContent = this.story.title;

    await this.metaStore.load();
    this.sceneManager.start();
    this.gameLoop();
    this.updateStatus();

    const save = await this.saveManager.load();
    if (save && save.storyId === this.story.id) {
      this.showBootMenu(save);
    } else {
      if (save && save.storyId && save.storyId !== this.story.id) {
        this.showToast("Old save belongs to another story - new sprint");
      }
      this.startNewGame(false);
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.sceneManager?.dispose();
    this.typewriter?.stop();
    this.container.innerHTML = "";
    this.sceneManager = null;
    this.narrativeEngine = null;
    this.typewriter = null;
  }

  private startNewGame(clearSave = true): void {
    if (!this.narrativeEngine || !this.sceneManager) return;
    this.currentSceneKey = null;
    this.currentFloorName = null;
    this.pendingFloorRequest = false;
    this.bossStarted = false;
    this.ending = null;
    this.runManager =
      this.story.schema === "roguelike"
        ? new RunManager(this.story.floors)
        : null;
    this.sceneManager.setBackgroundColor(DEFAULT_BG);

    this.narrativeEngine.loadStory(this.story.inkJson);
    this.updateStatus();

    if (clearSave) {
      void this.saveManager?.deleteSave();
    }

    this.showNextText();
  }

  private resumeGame(save: SaveData): void {
    if (!this.narrativeEngine || !this.sceneManager) return;
    this.currentFloorName = null;
    this.pendingFloorRequest = false;

    this.narrativeEngine.loadStory(this.story.inkJson);
    this.narrativeEngine.loadStateJson(save.storyState);

    const vars = this.narrativeEngine.getVariables();
    const bossHealth = Number(vars["boss_health"] ?? 8);
    this.bossStarted = save.sceneKey === "boss" || bossHealth < 8;
    this.currentSceneKey = save.sceneKey ?? null;
    this.sceneManager.setBackgroundColor(
      save.sceneKey
        ? (this.story.sceneColors[save.sceneKey] ?? DEFAULT_BG)
        : DEFAULT_BG
    );

    this.typewriter?.displayInstant(save.lastText);
    this.updateStatus();

    if (this.narrativeEngine.canContinue()) {
      this.showNextText();
    } else {
      this.showChoices();
    }
  }

  private showBootMenu(save: SaveData): void {
    const container = this.container.querySelector("#choices-container");
    if (!container) return;

    this.typewriter?.stop();
    this.sceneManager?.setBackgroundColor(
      save.sceneKey
        ? (this.story.sceneColors[save.sceneKey] ?? DEFAULT_BG)
        : DEFAULT_BG
    );

    const textElement = this.container.querySelector("#narrative-text");
    if (textElement) textElement.textContent = "";

    container.innerHTML = "";

    const title = document.createElement("div");
    title.className = "boot-title";
    title.textContent = "Save found";
    container.appendChild(title);

    const info = document.createElement("div");
    info.className = "boot-info";
    info.textContent = `${new Date(save.savedAt).toLocaleString()} - ${save.storyTitle ?? "unknown story"}`;
    container.appendChild(info);

    const resumeBtn = document.createElement("button");
    resumeBtn.className = "choice-button";
    resumeBtn.textContent = "Resume the sprint";
    resumeBtn.onclick = () => this.resumeGame(save);
    container.appendChild(resumeBtn);

    const newBtn = document.createElement("button");
    newBtn.className = "choice-button";
    newBtn.textContent = "Start a new sprint";
    newBtn.onclick = () => this.startNewGame();
    container.appendChild(newBtn);
  }

  private gameLoop(): void {
    const animate = (): void => {
      if (this.disposed) return;
      requestAnimationFrame(animate);
      const time = performance.now() / 1000;
      if (!this.devScene) return;

      const vars = this.narrativeEngine?.getVariables() ?? {};
      const health = Number(vars["health"] ?? MAX_HEALTH);
      const tokens = Number(vars["tokens"] ?? 0);
      const tier = Number(vars["tier"] ?? 1);
      const floorNumber = Number(vars["floor_number"] ?? 1);

      this.devScene.update({
        health,
        maxHealth: MAX_HEALTH,
        tokens,
        tier,
        depth: floorNumber,
        boss: this.bossStarted,
        time,
      });
    };
    animate();
  }

  private processTags(tags: string[]): void {
    for (const tag of tags) {
      const [kind, rawValue] = tag.split("::", 2);
      const value = (rawValue ?? "").trim();

      switch (kind) {
        case "SCENE":
          this.currentSceneKey = value;
          this.sceneManager?.setBackgroundColor(
            this.story.sceneColors[value] ?? DEFAULT_BG
          );
          break;
        case "ENDING":
          this.ending = value;
          this.currentSceneKey = value;
          this.sceneManager?.setBackgroundColor(
            this.story.sceneColors[value] ?? "#0a0a12"
          );
          break;
        case "NEXT_FLOOR":
          this.pendingFloorRequest = true;
          break;
        case "BOSS":
          this.bossStarted = true;
          this.showToast(this.story.messages.boss ?? "BOSS");
          break;
        case "DIE":
          this.sceneManager?.setBackgroundColor("#4a000a");
          this.showToast("DEVELOPER DOWN");
          break;
        case "GAME_OVER":
          this.sceneManager?.setBackgroundColor(
            this.story.sceneColors.fail ?? "#5a0000"
          );
          this.showToast(this.story.messages.game_over ?? "TERMINATED");
          break;
        case "MESSAGE":
          if (this.story.messages[value])
            this.showToast(this.story.messages[value]);
          break;
        default:
          break;
      }
    }
  }

  private showToast(text: string): void {
    const toast = this.container.querySelector("#toast");
    if (!toast) return;

    toast.textContent = text;
    toast.classList.add("show");

    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => toast.classList.remove("show"), 3000);
  }

  private updateStatus(): void {
    const vars = this.narrativeEngine?.getVariables() ?? {};
    const health = Number(vars["health"] ?? MAX_HEALTH);
    const tokens = Number(vars["tokens"] ?? 0);
    const floorNumber = Number(vars["floor_number"] ?? 1);
    const debuggerCharges = Number(vars["debugger_charges"] ?? 0);

    const hpFill = this.container.querySelector("#hp-fill");
    const hpValue = this.container.querySelector("#hp-value");
    const tagsEl = this.container.querySelector("#status-tags");

    if (hpFill) {
      const clamped = Math.max(0, Math.min(MAX_HEALTH, health));
      (hpFill as HTMLElement).style.width = `${clamped}%`;
      (hpFill as HTMLElement).style.background =
        health > 50 ? "#e94560" : health > 25 ? "#ffa500" : "#ff0000";
    }
    if (hpValue) hpValue.textContent = `${health}/${MAX_HEALTH}`;

    if (tagsEl) {
      tagsEl.innerHTML = "";
      const badges: string[] = [];
      if (this.story.schema === "roguelike") {
        if (tokens > 0) badges.push(`$${tokens}`);
        badges.push(`floor ${floorNumber}`);
        if (this.currentFloorName) badges.push(this.currentFloorName);
        if (debuggerCharges > 0) badges.push(`debugger x${debuggerCharges}`);
        if (this.bossStarted) badges.push("boss");
      } else {
        badges.push("adventure");
      }

      badges.forEach((label) => {
        const badge = document.createElement("span");
        badge.className = "badge";
        badge.textContent = label;
        tagsEl.appendChild(badge);
      });
    }
  }

  private buildSaveData(): SaveData | null {
    const storyState = this.narrativeEngine?.getStateJson();
    if (!storyState) return null;
    if (!this.typewriter) return null;

    return {
      version: 1,
      savedAt: Date.now(),
      storyState,
      sceneKey: this.currentSceneKey,
      lastText: this.typewriter.getText(),
      variables: this.narrativeEngine?.getVariables() ?? {},
      storyId: this.story.id,
      storyTitle: this.story.title,
    };
  }

  private persist(skipDebounce = false): void {
    if (!this.narrativeEngine || !this.typewriter) return;
    if (this.narrativeEngine.isComplete()) return;

    const now = Date.now();
    if (!skipDebounce && now - this.lastSaveAt < AUTO_SAVE_DEBOUNCE_MS) return;
    this.lastSaveAt = now;

    const data = this.buildSaveData();
    if (data) {
      void this.saveManager?.save(data);
    }
  }

  private manualSave(): void {
    const health = Number(this.narrativeEngine?.getVariable("health") ?? 0);
    const tokens = Number(this.narrativeEngine?.getVariable("tokens") ?? 0);

    if (
      !this.narrativeEngine ||
      this.narrativeEngine.isComplete() ||
      health <= 0
    ) {
      this.showToast("Sprint over - no save made");
      return;
    }
    this.persist(true);
    this.showToast(`Progress saved (HP ${health}, $${tokens})`);
  }

  private maybeAdvanceFloor(): void {
    if (!this.narrativeEngine || !this.runManager) return;
    if (!this.narrativeEngine.isComplete()) return;
    if (!this.pendingFloorRequest) return;
    this.pendingFloorRequest = false;

    const floorNumber = Number(
      this.narrativeEngine.getVariable("floor_number")
    );
    if (Number(this.narrativeEngine.getVariable("health")) <= 0) {
      this.showToast("DEVELOPER DOWN");
      this.narrativeEngine.choosePath("game_over");
      this.showNextText();
      return;
    }

    const tier = this.runManager.tierForDepth(floorNumber);
    const floor = this.runManager.pickFloor(floorNumber);
    this.currentFloorName = floor.nodeName;

    this.narrativeEngine.setVariable("tier", tier);
    this.typewriter?.displayInstant("");
    this.narrativeEngine.choosePath(floor.path);
    this.processTags(this.narrativeEngine.getCurrentTags());
    this.showNextText();
  }

  private handleEnding(): void {
    if (!this.ending) return;
    const depth = Number(
      this.narrativeEngine?.getVariable("floor_number") ?? 1
    );
    const survived = this.ending === "shipped";
    this.metaStore.recordRun(depth, survived);
    void this.metaStore.persist();
    this.ending = null;
  }

  private showNextText(): void {
    if (!this.narrativeEngine) return;
    if (this.narrativeEngine.canContinue()) {
      const text = this.narrativeEngine.continue();
      if (text) {
        this.processTags(this.narrativeEngine.getCurrentTags());
        this.typewriter?.start(text, () => {
          this.onParagraphComplete();
        });
      } else {
        this.onParagraphComplete();
      }
    } else {
      this.updateStatus();
      this.showChoices();
    }
  }

  private onParagraphComplete(): void {
    this.updateStatus();
    this.handleEnding();
    this.maybeAdvanceFloor();
    this.showChoices();
    if (this.narrativeEngine?.canContinue()) {
      this.addContinueButton();
    } else {
      this.persist();
    }
  }

  private showChoices(): void {
    const container = this.container.querySelector("#choices-container");
    if (!container) return;
    container.innerHTML = "";

    if (!this.narrativeEngine) return;

    if (this.narrativeEngine.isComplete()) {
      const restartBtn = document.createElement("button");
      restartBtn.className = "choice-button continue-btn";
      restartBtn.textContent = "\u21ba Retry the sprint";
      restartBtn.onclick = () => this.startNewGame(true);
      container.appendChild(restartBtn);

      const hubBtn = document.createElement("button");
      hubBtn.className = "choice-button";
      hubBtn.textContent = "Back to the hub";
      hubBtn.onclick = () => this.onExit?.();
      container.appendChild(hubBtn);
      return;
    }

    const choices = this.narrativeEngine.getChoices();
    choices.forEach((choice) => {
      const button = document.createElement("button");
      button.className = "choice-button";
      button.textContent = choice.text;
      button.onclick = () => {
        this.narrativeEngine?.chooseChoice(choice.index);
        this.processTags(this.narrativeEngine?.getCurrentTags() ?? []);
        this.showNextText();
      };
      container.appendChild(button);
    });
  }

  private addContinueButton(): void {
    const container = this.container.querySelector("#choices-container");
    if (!container) return;
    const existing = container.querySelector(".continue-btn");
    if (existing) return;

    const btn = document.createElement("button");
    btn.className = "choice-button continue-btn";
    btn.textContent = "Continue...";
    btn.onclick = () => {
      btn.remove();
      this.showNextText();
    };
    container.appendChild(btn);
  }
}
